import PartnerLinkButton from "@/app/components/atomic/organisms/PartnerLinkButton"
import ImageBlock from "@/app/components/auctor/tool/ImageBlock"
import TableBlock from "@/app/components/auctor/tool/TableBlock"
import { IAppliedFilter } from "@/app/models/auctor"
import { evaluateExpressionWithFiltersAndRowData, toSafeHtml } from "@/app/utils/expression-utils"
import dynamic from "next/dynamic"

const GaugeBlock = dynamic(() => import("@/app/components/auctor/tool/GaugeBlock"), {
    loading: () => <GaugeBlockSkeleton />,
})

interface ISectionBlockProps {
    block: any
    expressionContext?: string
    appliedFilters: IAppliedFilter[]
    toolData: any
    queryParams?: Record<string, string> | null
}

export default function SectionBlock({
    block,
    expressionContext,
    appliedFilters,
    toolData,
    queryParams,
}: ISectionBlockProps) {
    const buildUrlWithEvaluatedParams = (
        baseUrl: string | null | undefined,
        params: any[] | null | undefined
    ): string => {
        if (!baseUrl) return ""
        if (!params || params.length === 0) return baseUrl

        let urlObj: URL
        try {
            urlObj = new URL(baseUrl)
        } catch {
            const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost"
            try {
                urlObj = new URL(baseUrl, origin)
            } catch {
                return String(baseUrl)
            }
        }

        for (const param of params) {
            if (!param) continue

            // Support both camelCase and snake_case payloads + optional direct name
            const valueExpression = param.valueExpression ?? param.value_expression
            const nameFromColumnKey = param.nameTargetColumn ?? param.name_target_column
            const explicitName = param.name ?? null

            if (!valueExpression) continue

            try {
                const evaluatedValue = evaluateExpressionWithFiltersAndRowData(
                    valueExpression,
                    toolData,
                    appliedFilters,
                    expressionContext,
                    queryParams
                )

                if (evaluatedValue === null || evaluatedValue === undefined || String(evaluatedValue) === "") {
                    continue
                }

                // Resolve the parameter name
                let paramName: string | undefined
                if (typeof explicitName === "string" && explicitName.length > 0) {
                    paramName = explicitName
                } else if (typeof nameFromColumnKey === "string" && nameFromColumnKey.length > 0) {
                    const lookedUpName = toolData?.[nameFromColumnKey]
                    paramName =
                        typeof lookedUpName === "string" && lookedUpName.length > 0 ? lookedUpName : nameFromColumnKey
                }

                if (!paramName) continue

                urlObj.searchParams.set(paramName, String(evaluatedValue))
            } catch (error) {
                console.warn("Failed to evaluate tool button url param", param, error)
            }
        }

        return urlObj.toString()
    }

    const textAlign: "left" | "center" | "right" =
        block.type === "text" && ["left", "center", "right"].includes(block.text_align)
            ? block.text_align
            : "center"
    const outerJustify =
        textAlign === "left" ? "justify-start" : textAlign === "right" ? "justify-end" : "justify-center"

    return (
        <div className={`col-span-${block.col_span} flex ${outerJustify} items-center`}>
            {block.type === "text" && (
                <span
                    className={`text-${block.text_font_size} font-${block.text_font_weight} text-${textAlign}`}
                    style={{ color: block.text_color }}
                >
                    {block.text_content}
                </span>
            )}

            {block.type === "image" && (
                <ImageBlock
                    block={block}
                    appliedFilters={appliedFilters}
                    toolData={toolData}
                    expressionContext={expressionContext}
                    queryParams={queryParams}
                />
            )}

            {block.type === "expression" && (
                <div
                    className="w-full"
                    dangerouslySetInnerHTML={{
                        __html: toSafeHtml(
                            evaluateExpressionWithFiltersAndRowData(
                                block.expression,
                                toolData,
                                appliedFilters,
                                expressionContext,
                                queryParams
                            )
                        ),
                    }}
                />
            )}

            {block.type === "button" && (
                <PartnerLinkButton
                    target={buildUrlWithEvaluatedParams(
                        block.button_target_url,
                        // Support both possible payload keys coming from API
                        block.button_url_params ?? block.url_params
                    )}
                    size={block.button_size}
                    theme={block.button_theme}
                    label={block.button_label}
                />
            )}

            {block.type === "gauge" && (
                <GaugeBlock
                    block={block}
                    appliedFilters={appliedFilters}
                    toolData={toolData}
                    expressionContext={expressionContext}
                    queryParams={queryParams}
                />
            )}

            {block.type === "table" && (
                <TableBlock
                    block={block}
                    appliedFilters={appliedFilters}
                    toolData={toolData}
                    expressionContext={expressionContext}
                    queryParams={queryParams}
                />
            )}
        </div>
    )
}

function GaugeBlockSkeleton() {
    return (
        <div className={"flex flex-col justify-center items-center gap-2 relative"}>
            <div className={"w-[140px] h-[140px] bg-gray-400 rounded-full animate-pulse"} />
        </div>
    )
}
